import { blobToBase64 } from "./assets";

export const ACCEPTED_UPLOAD_TYPES = [
	"image/png",
	"image/jpeg",
	"image/webp",
] as const;

/** Files bigger than this aren't even decoded; they'd only be shrunk anyway. */
const MAX_SOURCE_BYTES = 40 * 1024 * 1024;
/** Tried in order until the re-encoded image fits the size limit. */
const ENCODE_QUALITIES = [0.9, 0.82, 0.74, 0.66, 0.58];

export interface UploadSizeLimits {
	maxDimension: number;
	maxImageBytes: number;
}

export interface PreparedUpload {
	fileName: string;
	imageBase64: string;
}

export function isAcceptedUploadType(type: string): boolean {
	return (ACCEPTED_UPLOAD_TYPES as readonly string[]).includes(type);
}

function canvasToBlob(
	canvas: HTMLCanvasElement,
	type: string,
	quality: number
): Promise<Blob | null> {
	return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Shrink an image to the upload limits in the browser: scale the longest side
 * down to `maxDimension` and re-encode (WebP, or JPEG where WebP encoding isn't
 * supported) until it fits `maxImageBytes`. Images already within both limits
 * are uploaded untouched.
 */
export async function prepareImageForUpload(
	file: File,
	limits: UploadSizeLimits
): Promise<PreparedUpload> {
	if (!isAcceptedUploadType(file.type)) {
		throw new Error("Only PNG, JPEG and WebP images can be uploaded.");
	}
	if (file.size > MAX_SOURCE_BYTES) {
		throw new Error("That image is too large (over 40 MB).");
	}

	const bitmap = await createImageBitmap(file);
	try {
		const longest = Math.max(bitmap.width, bitmap.height);
		const scale = Math.min(1, limits.maxDimension / longest);
		if (scale === 1 && file.size <= limits.maxImageBytes) {
			return { fileName: file.name, imageBase64: await blobToBase64(file) };
		}

		const canvas = document.createElement("canvas");
		canvas.width = Math.max(1, Math.round(bitmap.width * scale));
		canvas.height = Math.max(1, Math.round(bitmap.height * scale));
		const context = canvas.getContext("2d");
		if (!context) {
			throw new Error("Could not process the image.");
		}
		context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

		for (const quality of ENCODE_QUALITIES) {
			// biome-ignore lint/performance/noAwaitInLoops: tries qualities from best to worst and stops at the first that fits
			let blob = await canvasToBlob(canvas, "image/webp", quality);
			// Browsers without WebP encoding silently return a PNG instead.
			if (blob?.type !== "image/webp") {
				blob = await canvasToBlob(canvas, "image/jpeg", quality);
			}
			if (blob && blob.size <= limits.maxImageBytes) {
				return { fileName: file.name, imageBase64: await blobToBase64(blob) };
			}
		}
		throw new Error("Could not compress the image enough to upload it.");
	} finally {
		bitmap.close();
	}
}
